/**
 * Aufklappmenü für den Kopf: Konto, „Mehr", und auf schmalen Bildschirmen
 * die ganze Navigation.
 *
 * Schließt bei Klick daneben, bei Escape und bei jedem Seitenwechsel — ein
 * Menü, das nach dem Klick auf einen Eintrag offen über der neuen Seite
 * hängt, verdeckt genau das, wohin man wollte.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

export function Menu({
  label,
  title,
  align = "right",
  active = false,
  buttonClassName,
  panelClassName = "min-w-56",
  children,
}: {
  label: ReactNode;
  title?: string;
  align?: "left" | "right";
  /** Liegt die aktuelle Seite in diesem Menü? Dann wie ein aktiver Reiter. */
  active?: boolean;
  buttonClassName: string;
  panelClassName?: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className={`${buttonClassName} ${active || open ? "nav-link-active" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <span aria-hidden="true" className={`text-[10px] transition-transform ${open ? "rotate-180" : ""}`}>
          ▾
        </span>
      </button>
      {open ? (
        <div
          role="menu"
          className={`absolute top-full z-30 mt-1 rounded border border-line bg-page p-1 ${
            align === "right" ? "right-0" : "left-0"
          } ${panelClassName}`}
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}
